import { getMailComposeProjectionDefinitionMLR } from './MLR/mailComposeProjectionDefinitionMLR';
import { getMailComposeProjectionDefinitionSLR } from './SLR/mailComposeProjectionDefinitionSLR';
import { useWindow } from '@fluentui/react/lib/WindowProvider';
import { observer } from 'mobx-react-lite';
import { attachmentMultiMenuEntry } from 'owa-attachment-multi-menu-actions';
import { CommandRibbon } from 'owa-command-ribbon';
import { getIsViewModeSelected } from 'owa-command-ribbon-store';
import loadAttachmentSuggestions from 'owa-mail-compose-actions/lib/utils/loadAttachmentSuggestions';
import { getCreateAttachmentHandlers } from 'owa-mail-compose-view/lib/utils/getCreateAttachmentHandlers';
import { MailRibbonTabId } from 'owa-ribbon-ids';
import { getMailIconColorMap } from 'owa-mail-ribbon/lib/util/mailIconColorMap';
import { CommandingViewMode } from 'owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions';
import React, { useMemo } from 'react';
import {
    AttachmentLocalComputerFilePicker,
    FilePickerAction,
    FileSelectionFilter,
    IAttachmentLocalComputerFilePicker,
} from 'owa-attachment-filepicker';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type AttachmentMultiPicker from 'owa-mail-compose-view/lib/types/AttachmentMultiPicker';
import type CreateAttachmentHandlers from 'owa-mail-compose-view/lib/types/CreateAttachmentHandlers';

export interface MailComposeProjectionRibbonProps {
    popoutComposeViewState: ComposeViewState;
}

export default observer(function MailComposeProjectionRibbon(
    props: MailComposeProjectionRibbonProps
) {
    // Post-render pre-loads
    React.useEffect(() => {
        if (props.popoutComposeViewState) {
            loadAttachmentSuggestions(props.popoutComposeViewState);
        }
    }, []);

    const targetWindow: Window = useWindow() ?? window;

    const attachmentMultiPicker: AttachmentMultiPicker = {
        computer: React.useRef<React.RefObject<IAttachmentLocalComputerFilePicker>>(
            React.createRef()
        ),
        onlineLocation: React.useRef<React.RefObject<IAttachmentLocalComputerFilePicker>>(
            React.createRef()
        ),
        uploadAndShareFromCloud: React.useRef<React.RefObject<IAttachmentLocalComputerFilePicker>>(
            React.createRef()
        ),
        targetWindow: targetWindow,
    };

    const attachmentHandlers: CreateAttachmentHandlers = getCreateAttachmentHandlers(
        attachmentMultiPicker,
        attachmentMultiMenuEntry,
        props.popoutComposeViewState
    );

    const attachmentPickers = {
        addFilesAsAttachments: attachmentMultiPicker,
    };

    const { getSingleLineRibbonDefinition, getMultiLineRibbonDefinition } = useMemo(() => {
        const ribbonProps = {
            showMoveRules: true,
            attachmentPickers,
            targetWindow,
            popoutComposeViewState: props.popoutComposeViewState,
        };
        return {
            getSingleLineRibbonDefinition: () => getMailComposeProjectionDefinitionSLR(ribbonProps),
            getMultiLineRibbonDefinition: () => getMailComposeProjectionDefinitionMLR(ribbonProps),
        };
    }, [props.popoutComposeViewState]);

    return (
        <span>
            <AttachmentLocalComputerFilePicker
                ref={attachmentMultiPicker.computer.current}
                fileSelectionFilter={FileSelectionFilter.None}
                action={FilePickerAction.Attach}
                createAttachmentHandler={attachmentHandlers.computer}
            />
            <AttachmentLocalComputerFilePicker
                ref={attachmentMultiPicker.onlineLocation.current}
                fileSelectionFilter={FileSelectionFilter.None}
                action={FilePickerAction.Attach}
                createAttachmentHandler={attachmentHandlers.uploadAndLinkFromCloud}
            />
            <AttachmentLocalComputerFilePicker
                ref={attachmentMultiPicker.uploadAndShareFromCloud.current}
                fileSelectionFilter={FileSelectionFilter.None}
                action={FilePickerAction.InsertAsLink}
                createAttachmentHandler={attachmentHandlers.uploadAndShareFromCloud}
            />
            <CommandRibbon
                getSingleLineRibbonDefinition={getSingleLineRibbonDefinition}
                getMultiLineRibbonDefinition={getMultiLineRibbonDefinition}
                getDefaultSelectedTabId={getDefaultSelectedTabId}
                getHideTopRibbon={getHideTopRibbon}
                iconColorMap={getMailIconColorMap()}
                thisWindow={targetWindow}
            />
        </span>
    );
});

function getHideTopRibbon() {
    return getIsViewModeSelected(CommandingViewMode.CommandBar);
}

function getDefaultSelectedTabId() {
    return MailRibbonTabId.Tab_Message;
}
